import { supabase } from "../supabaseClient";
import { SelectedFile } from "../types/PostSelection";
import { SavedContent } from "../types/api/SavedContent";
import { getMediaPath, MEDIA_SIZE } from "../api/files";
import toast from "react-hot-toast";

/**
 * Interface for photo metadata that contains searchable text
 */
interface PhotoMetadata {
  // EXIF metadata can contain various text fields
  [key: string]: any;
  // Common EXIF fields
  Make?: string;
  Model?: string;
  Lens?: string;
  // Common EXIF fields that might contain searchable text
  XPTitle?: string;
  XPComment?: string;
  XPAuthor?: string;
  XPKeywords?: string;
  XPSubject?: string;
  ImageDescription?: string;
  UserComment?: string;
  // IPTC fields
  Caption?: string;
  Keywords?: string[];
  Headline?: string;
  Category?: string;
  SupplementalCategories?: string[];
  // XMP fields
  Title?: string;
  Description?: string;
  Subject?: string[];
  Creator?: string;
}

/**
 * Extracts searchable text from photo metadata
 */
function extractSearchableText(metadata: PhotoMetadata): string {
  return JSON.stringify(metadata);
}

/**
 * Gets photo metadata for selected files
 */
async function getSelectedFilesMetadata(
  selectedFiles: SelectedFile[]
): Promise<PhotoMetadata[]> {
  try {
    const originalPath = await getMediaPath(MEDIA_SIZE.original);

    // Create paths for the selected files
    const filePaths = selectedFiles.map(
      (file) => `${originalPath}/${file.name}`
    );

    // Query metadata for all selected files
    const { data: metadataResults, error } = await supabase
      .from("media_metadata")
      .select("path, metadata")
      .in("path", filePaths);

    if (error) {
      console.error("Error fetching metadata:", error);
      return [];
    }

    return metadataResults as PhotoMetadata[];
  } catch (error) {
    console.error("Error getting photo metadata:", error);
    return [];
  }
}

/**
 * Gets all saved content with auto_add_search configured
 */
async function getSavedContentWithAutoAdd(): Promise<SavedContent[]> {
  try {
    const { data, error } = await supabase
      .from("saved_content")
      .select("*")
      .not("auto_add_search", "is", null)
      .neq("auto_add_search", "");

    if (error) {
      console.error("Error fetching saved content:", error);
      return [];
    }

    return (data as SavedContent[]) || [];
  } catch (error) {
    console.error("Error getting saved content:", error);
    return [];
  }
}

/**
 * Main function to check selected photos and return matching saved content
 */
export async function getAutoAddSavedContent(
  selectedFiles: SelectedFile[]
): Promise<SavedContent[]> {
  if (!selectedFiles || selectedFiles.length === 0) {
    return [];
  }

  try {
    // Get metadata for selected files
    const metadataList = await getSelectedFilesMetadata(selectedFiles);

    if (metadataList.length === 0) {
      console.log("No metadata found for selected files");
      return [];
    }

    // Get saved content with auto_add_search configured
    const savedContentList = await getSavedContentWithAutoAdd();

    if (savedContentList.length === 0) {
      console.log("No saved content with auto_add_search found");
      return [];
    }

    const matchingSavedContent: SavedContent[] = [];

    // Check each saved content against all selected files
    for (const savedContent of savedContentList) {
      if (!savedContent.auto_add_search) continue;

      let hasMatch = false;

      for (const metadata of metadataList) {
        const searchableText = extractSearchableText(metadata);

        if (searchableText.includes(savedContent.auto_add_search)) {
          hasMatch = true;
          break; // Found a match, no need to check other metadata for this saved content
        }
      }

      if (
        hasMatch &&
        !matchingSavedContent.find((sc) => sc.id === savedContent.id)
      ) {
        matchingSavedContent.push(savedContent);
      }
    }

    console.log(
      `Found ${matchingSavedContent.length} matching saved content items`
    );
    return matchingSavedContent;
  } catch (error) {
    console.error("Error in getAutoAddSavedContent:", error);
    return [];
  }
}

export async function autoAddSavedContentToForm(
  selectedFiles: SelectedFile[],
  setSelectedSavedDescriptions: (callback: (prev: number[]) => number[]) => void
): Promise<void> {
  try {
    const matchingSavedContent = await getAutoAddSavedContent(selectedFiles);

    if (matchingSavedContent.length === 0) {
      return;
    }

    console.log("Auto-adding saved content:", matchingSavedContent);

    // Add each matching saved content to the form
    for (const savedContent of matchingSavedContent) {
      setSelectedSavedDescriptions((prev) => {
        if (prev.includes(savedContent.id as number)) {
          return prev;
        }
        return [...prev, savedContent.id as number];
      });

      // setValue(
      //   "default.post_content",
      //   watch("default.post_content") +
      //     `{{VARIANTS(${savedContent.id}): "${savedContent.content}"}}`
      // );

      // Add main hashtags to default
      // const mainHashtags = savedContent.hashtags?.main || [];
      // const currentHashtags = watch("default.hashtags") ?? [];
      // setValue("default.hashtags", [
      //   `#VARIANTS(${savedContent.id}): ${(mainHashtags ?? []).join(" ")}`,
      //   ...currentHashtags,
      // ]);
    }

    console.log(
      `Auto-added ${matchingSavedContent.length} saved content items to form`
    );

    // Show success notification
    if (matchingSavedContent.length > 0) {
      const names = matchingSavedContent.map((sc) => sc.name).join(", ");
      toast.success(
        `Auto-added ${matchingSavedContent.length} saved description${
          matchingSavedContent.length > 1 ? "s" : ""
        }: ${names}`
      );
    }
  } catch (error) {
    console.error("Error auto-adding saved content to form:", error);
  }
}
